/* eslint-disable react/display-name */
import { Suspense } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
} from "react-router-dom";
import { wrapCreateBrowserRouter } from "@sentry/react";

import { ROUTES, SEGMENTS } from "@/constants/routes.constants";
import { routes } from "@/lib/utils";
import {
  agentConfigurationNavPages,
  clinicConfigurationNavPages,
} from "@/lib/utils/clinic-configuration/config-nav";
import { lazyPreload } from "@/lib/utils/lazyPreload";
import { AgentProvider } from "@/context/agent-provider";
import { ProjectProvider } from "@/context/project-provider";
import { UserProvider } from "@/context/user-provider";

import { AccessRoute } from "@/components/module/AccessRoute";
import { GuestRoute } from "@/components/module/GuestRoute";
import { Layout } from "@/components/module/Layout";
import { Loading } from "@/components/module/Loading";
import { ProtectedRoute } from "@/components/module/ProtectedRoute";
import { SuperAdminRoute } from "@/components/module/SuperAdminRoute";

import { GoogleCalendarCallbackPage } from "./Integrations/GoogleCalendarCallback";
import { LoginPage } from "./Login";
import { NotFound } from "./NotFound";
import { ResetPasswordPage } from "./ResetPasswordPage";
import { SetPasswordPage } from "./SetPasswordPage";

const LazySettingsProfile = lazyPreload(async () => {
  const { ProfilePage } = await import("./Profile");
  return ProfilePage;
});

const LazySettingsProject = lazyPreload(async () => {
  const { ProjectPage } = await import("./Project");
  return ProjectPage;
});

const LazySettingsTeam = lazyPreload(async () => {
  const { TeamPage } = await import("./Team");
  return TeamPage;
});

const LazySettingsWhatsapp = lazyPreload(async () => {
  const { WhatsappPage } = await import("./Whatsapp");
  return WhatsappPage;
});

const LazySettingsAnalytics = lazyPreload(async () => {
  const { AnalyticsPage } = await import("./Analytics");
  return AnalyticsPage;
});

const LazySettingsPractitioners = lazyPreload(async () => {
  const { PractitionersPage } = await import("./Practitioners");
  return PractitionersPage;
});

const LazyMySchedule = lazyPreload(async () => {
  const { MySchedulePage } = await import("./MySchedule");
  return MySchedulePage;
});

const LazyPractitionerScheduling = lazyPreload(async () => {
  const [{ PractitionerSchedulingPage }] = await Promise.all([
    import("./PractitionerScheduling"),
    import("@/lib/dayjs"),
  ]);
  return PractitionerSchedulingPage;
});

const LazyPractitionerAvailability = lazyPreload(async () => {
  const [{ PractitionerAvailabilityPage }] = await Promise.all([
    import("./PractitionerAvailability"),
    import("@/lib/dayjs"),
  ]);
  return PractitionerAvailabilityPage;
});

const LazyKoraConfiguration = lazyPreload(async () => {
  const [{ KoraConfigurationPage }] = await Promise.all([
    import("./AgentConfiguration/KoraConfiguration"),
    import("@/lib/dayjs"),
  ]);
  return KoraConfigurationPage;
});

/**
 * Imported by file rather than through the folder: a barrel would put both
 * agent configuration pages in one chunk, so opening either would download
 * the other.
 */
const LazyClinicFollowUps = lazyPreload(async () => {
  const { ClinicFollowUpsPage } =
    await import("./AgentConfiguration/ClinicFollowUps");
  return ClinicFollowUpsPage;
});

const LazyClinicEscalationConfiguration = lazyPreload(async () => {
  const { ClinicEscalationConfigurationPage } =
    await import("./AgentConfiguration/ClinicEscalationConfiguration");
  return ClinicEscalationConfigurationPage;
});

const LazyClinicDetailsPage = lazyPreload(async () => {
  const { ClinicDetailsPage } =
    await import("./ClinicConfiguration/ClinicDetails");
  return ClinicDetailsPage;
});

/**
 * The list and its drawer come from one module, so opening a practitioner's URL
 * directly loads both at once.
 */
const LazyPractitionersPage = lazyPreload(async () => {
  const { PractitionersPage } =
    await import("./ClinicConfiguration/Practitioners");
  return PractitionersPage;
});

const LazyPractitionerDrawer = lazyPreload(async () => {
  const { PractitionerDrawer } =
    await import("./ClinicConfiguration/Practitioners");
  return PractitionerDrawer;
});

const LazyPractitionerCreateDrawer = lazyPreload(async () => {
  const { PractitionerCreateDrawer } =
    await import("./ClinicConfiguration/Practitioners");
  return PractitionerCreateDrawer;
});

/**
 * One module for the list and both drawers, as for Practitioners.
 */
const LazyLocationsPage = lazyPreload(async () => {
  const { LocationsPage } = await import("./ClinicConfiguration/Locations");
  return LocationsPage;
});

const LazyLocationDrawer = lazyPreload(async () => {
  const { LocationDrawer } = await import("./ClinicConfiguration/Locations");
  return LocationDrawer;
});

const LazyLocationCreateDrawer = lazyPreload(async () => {
  const { LocationCreateDrawer } =
    await import("./ClinicConfiguration/Locations");
  return LocationCreateDrawer;
});

const LazyServicesPage = lazyPreload(async () => {
  const { ServicesPage } = await import("./ClinicConfiguration/Services");
  return ServicesPage;
});

const LazyServiceDrawer = lazyPreload(async () => {
  const { ServiceDrawer } = await import("./ClinicConfiguration/Services");
  return ServiceDrawer;
});

const LazyServiceCreateDrawer = lazyPreload(async () => {
  const { ServiceCreateDrawer } =
    await import("./ClinicConfiguration/Services");
  return ServiceCreateDrawer;
});

/**
 * Lazy so the `ConfigSection` barrel it imports stays out of the main chunk.
 */
const LazyClinicConfigurationGate = lazyPreload(async () => {
  const { ClinicConfigurationGate } =
    await import("@/components/module/ClinicConfigurationGate");
  return ClinicConfigurationGate;
});

const sentryCreateBrowserRouter = wrapCreateBrowserRouter(createBrowserRouter);

const appRouter = sentryCreateBrowserRouter([
  {
    path: ROUTES.GOOGLE_CALENDAR_CALLBACK,
    element: <GoogleCalendarCallbackPage />,
  },
  {
    element: <GuestRoute />,
    children: [
      {
        path: ROUTES.LOGIN,
        element: <LoginPage />,
      },
      {
        path: ROUTES.RESET_PASSWORD,
        element: <ResetPasswordPage />,
      },
      {
        path: ROUTES.SET_PASSWORD,
        element: <SetPasswordPage />,
      },
    ],
  },
  {
    element: (
      <UserProvider>
        <Outlet />
      </UserProvider>
    ),
    children: [
      {
        element: <ProtectedRoute />,
        children: [
          {
            element: (
              <ProjectProvider>
                <AgentProvider>
                  <Layout />
                </AgentProvider>
              </ProjectProvider>
            ),
            hasErrorBoundary: true,
            children: [
              {
                index: true,
                path: SEGMENTS.PROFILE,
                element: <LazySettingsProfile />,
              },
              {
                path: SEGMENTS.PROJECT,
                element: <LazySettingsProject />,
              },
              {
                element: <AccessRoute requires="canReadMembers" />,
                children: [
                  {
                    path: SEGMENTS.TEAM,
                    element: <LazySettingsTeam />,
                  },
                ],
              },
              {
                element: <AccessRoute requires="canManageWhatsApp" />,
                children: [
                  {
                    path: SEGMENTS.WHATSAPP,
                    element: <LazySettingsWhatsapp />,
                  },
                ],
              },
              {
                element: <AccessRoute requires="canViewAnalytics" />,
                children: [
                  {
                    path: routes.analyticsDashboard(),
                    element: <LazySettingsAnalytics />,
                  },
                ],
              },
              {
                element: <AccessRoute requires="canManageAllPractitioners" />,
                children: [
                  {
                    path: SEGMENTS.PRACTITIONERS,
                    element: <LazySettingsPractitioners />,
                  },
                ],
              },
              {
                element: <AccessRoute requires="canManageOwnSchedule" />,
                children: [
                  {
                    path: SEGMENTS.MY_SCHEDULE,
                    element: <LazyMySchedule />,
                  },
                ],
              },
              {
                element: <SuperAdminRoute />,
                children: [
                  {
                    path: SEGMENTS.AGENT_CONFIGURATION,
                    // t('Agent configuration')
                    element: (
                      <LazyClinicConfigurationGate
                        titleKey="Agent configuration"
                        pages={agentConfigurationNavPages}
                      />
                    ),
                    children: [
                      {
                        index: true,
                        element: <Navigate to={ROUTES.AGENT_KORA} />,
                      },
                      {
                        path: SEGMENTS.AGENT_KORA,
                        element: <LazyKoraConfiguration />,
                      },
                      {
                        path: SEGMENTS.AGENT_FOLLOW_UPS,
                        element: <LazyClinicFollowUps />,
                      },
                      {
                        path: SEGMENTS.AGENT_ESCALATION_CONFIGURATION,
                        element: <LazyClinicEscalationConfiguration />,
                      },
                    ],
                  },
                  {
                    path: SEGMENTS.CLINIC_CONFIGURATION,
                    // t('Clinic configuration')
                    element: (
                      <LazyClinicConfigurationGate
                        titleKey="Clinic configuration"
                        pages={clinicConfigurationNavPages}
                      />
                    ),
                    children: [
                      {
                        index: true,
                        element: <Navigate to={ROUTES.CLINIC_DETAILS} />,
                      },
                      {
                        path: SEGMENTS.CLINIC_DETAILS,
                        element: <LazyClinicDetailsPage />,
                      },
                      {
                        path: SEGMENTS.CLINIC_PRACTITIONERS,
                        element: <LazyPractitionersPage />,
                        children: [
                          {
                            path: SEGMENTS.CREATE_RESOURCE,
                            element: <LazyPractitionerCreateDrawer />,
                          },
                          {
                            path: ":id",
                            element: <LazyPractitionerDrawer />,
                          },
                        ],
                      },
                      {
                        path: SEGMENTS.CLINIC_LOCATIONS,
                        element: <LazyLocationsPage />,
                        children: [
                          {
                            path: SEGMENTS.CREATE_RESOURCE,
                            element: <LazyLocationCreateDrawer />,
                          },
                          {
                            path: ":id",
                            element: <LazyLocationDrawer />,
                          },
                        ],
                      },
                      {
                        path: SEGMENTS.CLINIC_SERVICES,
                        element: <LazyServicesPage />,
                        children: [
                          {
                            path: SEGMENTS.CREATE_RESOURCE,
                            element: <LazyServiceCreateDrawer />,
                          },
                          {
                            path: ":id",
                            element: <LazyServiceDrawer />,
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                path: ROUTES.HOME,
                element: <Navigate to={ROUTES.PROFILE} />,
              },
              {
                path: routes.practitionerScheduling(),
                element: <LazyPractitionerScheduling />,
              },
              {
                path: routes.practitionerAvailability(),
                element: <LazyPractitionerAvailability />,
              },
              {
                path: "*",
                element: <NotFound />,
              },
            ],
          },
        ],
      },
    ],
  },
]);

export const AppRouter: React.FC = () => {
  return (
    <Suspense fallback={<Loading />}>
      <RouterProvider router={appRouter} />
    </Suspense>
  );
};
